import type { Event } from '@sentry/types';

const REDACTED = '[redacted]';

const EMAIL_REGEX = /[\w.+-]+@[\w-]+\.[\w.-]+/g;

const PHONE_SEPARATED_REGEX =
  /(?:\+?55[\s-]?)?(?:\(\d{2}\)[\s-]?|\b\d{2}[\s-])?\b\d{4,5}[\s.-]\d{4}\b/g;
const PHONE_COMPACT_REGEX = /(?:\+?55)?\b\d{2}9\d{8}\b/g;

const CPF_REGEX = /\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/g;
const CNPJ_REGEX = /\b\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}\b/g;

const normalizeKey = (key: string) =>
  key.toLowerCase().replace(/[^a-z0-9]/g, '');

const SENSITIVE_KEY_STEMS = ['password', 'senha', 'token', 'secret', 'apikey'];

const SENSITIVE_KEYS = new Set(
  [
    'authorization',
    'cookie',
    'set-cookie',
    'session',
    'session_id',
    'gi_byps_rl',
    'cpf',
    'cnpj',
    'rg',
    'document',
    'ip',
    'ip_address',
    'remote_addr',
    'x_forwarded_for',
    'x_real_ip',
    'x_client_ip',
    'true_client_ip',
    'cf_connecting_ip',
    'forwarded',
    'username',
    'customer_name',
    'full_name',
    'fullname',
    'first_name',
    'last_name',
    'nome',
    'nome_completo',
    'email',
    'phone',
    'phone_number',
    'telephone',
    'cellphone',
    'telefone',
    'celular',
    'card',
    'card_number',
    'cvv',
    'birthdate',
    'birth_date',
  ].map(normalizeKey)
);

const PERSON_CONTAINER_KEYS = new Set(
  [
    'customer',
    'cliente',
    'user',
    'usuario',
    'guest',
    'client',
    'holder',
    'contact',
    'contato',
    'responsavel',
    'reservation',
    'reserva',
  ].map(normalizeKey)
);

const NAME_KEYS = new Set(['name', 'nome'].map(normalizeKey));

const isSensitiveKey = (key: string) => {
  const normalized = normalizeKey(key);

  return (
    SENSITIVE_KEYS.has(normalized) ||
    SENSITIVE_KEY_STEMS.some((stem) => normalized.includes(stem))
  );
};

const isPersonName = (key: string, parentKey?: string) =>
  NAME_KEYS.has(normalizeKey(key)) &&
  !!parentKey &&
  PERSON_CONTAINER_KEYS.has(normalizeKey(parentKey));

const scrubString = (value: string): string =>
  value
    .replace(CNPJ_REGEX, REDACTED)
    .replace(CPF_REGEX, REDACTED)
    .replace(EMAIL_REGEX, REDACTED)
    .replace(PHONE_SEPARATED_REGEX, REDACTED)
    .replace(PHONE_COMPACT_REGEX, REDACTED);

const scrub = (value: unknown, depth = 0, parentKey?: string): unknown => {
  if (depth > 6 || value == null) return value;
  if (typeof value === 'string') return scrubString(value);
  if (Array.isArray(value))
    return value.map((v) => scrub(v, depth + 1, parentKey));

  if (typeof value === 'object') {
    const prototype = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) {
      return scrubString(String(value));
    }

    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      result[key] =
        isSensitiveKey(key) || isPersonName(key, parentKey)
          ? REDACTED
          : scrub(val, depth + 1, key);
    }
    return result;
  }

  return value;
};

const scrubRecord = <T extends Record<string, unknown>>(
  record: T,
  parentKey?: string
): T => scrub(record, 0, parentKey) as T;

export const sanitizeSentryEvent = <T extends Event>(event: T): T => {
  if (event.request) {
    const { request } = event;

    if (request.headers) {
      request.headers = Object.fromEntries(
        Object.entries(request.headers).map(([key, value]) => [
          key,
          isSensitiveKey(key) ? REDACTED : scrubString(String(value)),
        ])
      );
    }

    delete request.cookies;

    if (request.data !== undefined) request.data = scrub(request.data);
    if (request.query_string) {
      request.query_string = scrub(
        request.query_string
      ) as typeof request.query_string;
    }
    if (request.url) request.url = scrubString(request.url);
  }

  if (event.user) {
    const { id, ...rest } = event.user;
    event.user = { ...(scrubRecord(rest, 'user') as typeof rest), id };
  }

  if (event.message) event.message = scrubString(event.message);

  if (event.exception?.values) {
    event.exception.values = event.exception.values.map((ex) => ({
      ...ex,
      value: ex.value ? scrubString(ex.value) : ex.value,
    }));
  }

  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((bc) => ({
      ...bc,
      message: bc.message ? scrubString(bc.message) : bc.message,
      data: bc.data ? scrubRecord(bc.data) : bc.data,
    }));
  }

  if (event.extra) event.extra = scrubRecord(event.extra);
  if (event.contexts) {
    event.contexts = scrub(event.contexts) as typeof event.contexts;
  }

  return event;
};
